import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { insertCarrierSchema } from "@/../../shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Download, Loader2 } from "lucide-react";
import FMCSADataPreviewModal from "./fmcsa-data-preview-modal";

const formSchema = insertCarrierSchema.extend({
  name: z.string().min(1, "Carrier name is required"),
  email: z.string().email("Valid email is required"),
  companyId: z.number().min(1, "Company is required"),
  docketPrefix: z.enum(["MC", "FF", "MX"]),
});

type FormData = z.infer<typeof formSchema>;

interface EditCarrierModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: FormData) => void;
  isLoading: boolean;
  carrier: any;
  companies: any[];
}

export default function EditCarrierModal({
  open,
  onClose,
  onSubmit,
  isLoading,
  carrier,
  companies,
}: EditCarrierModalProps) {
  const { toast } = useToast();
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [fetchedData, setFetchedData] = useState<any>(null);
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      address: "",
      docketPrefix: "MC" as const,
      docketNumber: "",
      companyId: 0,
    },
  });

  useEffect(() => {
    if (carrier) {
      form.reset({
        name: carrier.name || "",
        email: carrier.email || "",
        phone: carrier.phone || "",
        address: carrier.address || "",
        docketPrefix: carrier.docketPrefix || "MC",
        docketNumber: carrier.docketNumber || "",
        companyId: carrier.companyId || 0,
      });
    }
  }, [carrier, form]);

  const fetchFMCSADataMutation = useMutation({
    mutationFn: async () => {
      const docketPrefix = form.getValues('docketPrefix');
      const docketNumber = form.getValues('docketNumber');
      
      if (!docketNumber) {
        throw new Error("Docket number is required to fetch FMCSA data");
      }
      
      const response = await apiRequest("POST", "/api/fmcsa/fetch-by-docket", {
        docketPrefix,
        docketNumber,
      });
      
      // Parse the JSON response
      const data = await response.json();
      return data;
    },
    onSuccess: (data) => {
      setFetchedData(data);
      setShowPreviewModal(true);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to fetch FMCSA data",
        variant: "destructive",
      });
    },
  });

  const handleFetchFMCSAData = () => {
    fetchFMCSADataMutation.mutate();
  };

  const updateCarrierMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("PUT", `/api/carriers/${carrier.id}`, data);
      return response.json();
    },
    onSuccess: () => {
      // Invalidate all carrier-related queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['/api/carriers'] });
      queryClient.invalidateQueries({ queryKey: ['/api/carriers', carrier.id] });
      
      toast({
        title: "Success",
        description: "Carrier updated successfully with FMCSA data.",
      });
      setShowPreviewModal(false);
      setFetchedData(null);
      onClose();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update carrier",
        variant: "destructive",
      });
    },
  });

  const handleConfirmFMCSAData = () => {
    if (fetchedData && carrier) {
      // Prepare the complete update payload with all FMCSA fields
      const updateData = {
        ...carrier,
        name: fetchedData.name || carrier.name,
        legalName: fetchedData.legalName,
        dbaName: fetchedData.dbaName,
        email: fetchedData.email || carrier.email,
        phone: fetchedData.phone || carrier.phone,
        address: fetchedData.address || carrier.address,
        mailingAddress: fetchedData.mailingAddress,
        docketPrefix: fetchedData.docketPrefix || carrier.docketPrefix,
        docketNumber: fetchedData.docketNumber || carrier.docketNumber,
        
        // Authority status fields
        commonAuthorityStatus: fetchedData.commonAuthorityStatus,
        contractAuthorityStatus: fetchedData.contractAuthorityStatus,
        brokerAuthorityStatus: fetchedData.brokerAuthorityStatus,
        
        // Pending flags
        commonApplicationPending: fetchedData.commonApplicationPending,
        contractApplicationPending: fetchedData.contractApplicationPending,
        brokerApplicationPending: fetchedData.brokerApplicationPending,
        commonRevocationPending: fetchedData.commonRevocationPending,
        contractRevocationPending: fetchedData.contractRevocationPending,
        brokerRevocationPending: fetchedData.brokerRevocationPending,
        
        // Operating authority
        propertyCarriage: fetchedData.propertyCarriage,
        passengerCarriage: fetchedData.passengerCarriage,
        householdGoodsCarriage: fetchedData.householdGoodsCarriage,
        privateCarriage: fetchedData.privateCarriage,
        enterpriseCarriage: fetchedData.enterpriseCarriage,
        
        // Insurance
        bipdRequired: fetchedData.bipdRequired,
        bipdOnFile: fetchedData.bipdOnFile,
        cargoRequired: fetchedData.cargoRequired,
        cargoOnFile: fetchedData.cargoOnFile,
        bondRequired: fetchedData.bondRequired,
        bondOnFile: fetchedData.bondOnFile,
        
        // Mail delivery
        undeliverableMail: fetchedData.undeliverableMail,
      };
      
      updateCarrierMutation.mutate(updateData);
    }
  };

  const handleFormSubmit = (data: FormData) => {
    onSubmit(data);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Edit Carrier</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Carrier Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter carrier name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="companyId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company</FormLabel>
                    <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select company" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.id.toString()}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="carrier@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="(555) 123-4567" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Carrier address" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <FormField
                  control={form.control}
                  name="docketPrefix"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Docket Prefix</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger data-testid="select-docket-prefix">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="MC">MC</SelectItem>
                          <SelectItem value="FF">FF</SelectItem>
                          <SelectItem value="MX">MX</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="col-span-2">
                  <FormField
                    control={form.control}
                    name="docketNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Docket Number</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter docket number" data-testid="input-docket-number" {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                className="w-full"
                onClick={handleFetchFMCSAData}
                disabled={!form.watch('docketNumber') || fetchFMCSADataMutation.isPending}
                data-testid="button-fetch-fmcsa-data"
              >
                {fetchFMCSADataMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Fetching FMCSA Data...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Fetch FMCSA Data
                  </>
                )}
              </Button>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
      
      {showPreviewModal && fetchedData && (() => {
        // Get the latest carrier data from the cache
        const rawCarriers = queryClient.getQueryData(['/api/carriers']) as any;
        const carriersList: any[] = rawCarriers?.data ?? (Array.isArray(rawCarriers) ? rawCarriers : []);
        const latestCarrier = carriersList?.find((c: any) => c.id === carrier.id) || carrier;
        
        return (
          <FMCSADataPreviewModal
            open={showPreviewModal}
            onClose={() => {
              setShowPreviewModal(false);
              setFetchedData(null);
            }}
            onConfirm={handleConfirmFMCSAData}
            currentData={latestCarrier}
            fetchedData={fetchedData}
          />
        );
      })()}
    </Dialog>
  );
}